import uuid from "uuid";
import SaveEstimateRequest from "./save-estimate-request";
import EstimateGateway from "./estimate-gateway";
import Estimate from "./estimate";
import EstimateItem from "./estimate-item";
import UnitPrice from "@/orders/domain/models/unit-price";
import { toEstimate } from "@/orders/application/requests/estimate-request";
import { roundPrice } from "@/models/round-price";
import Decimal from "decimal.js";
import TotalPrice from "@/orders/domain/models/total-price";
import EstimateStatus from "@/usecases/orders/estimate-status";

export default class EstimateService {
  public constructor(private estimates: EstimateGateway) {}

  public async find(id: number, hasChild: boolean = true): Promise<Estimate> {
    const e = hasChild
      ? await this.estimates.find(id)
      : await this.estimates.findEstimateWithoutTree(id);
    if (!e) {
      throw new Error("estimate is not found.");
    }
    return e;
  }

  public async save(estimate: SaveEstimateRequest): Promise<number> {
    estimate.orderNew = 1;
    return await this.estimates.save(estimate);
  }

  public async copy(estimateId: number): Promise<number> {
    return await this.estimates.copy(estimateId);
  }

  public async changeStatus(
    id: string,
    status: string,
    hasChild: boolean,
    contractDate: string
  ): Promise<void> {
    const estimate = await this.find(Number(id), hasChild);
    estimate.status = status;
    estimate.orderNew = 1;
    if (estimate.status === EstimateStatus.Success) {
      estimate.dateOfContract = contractDate;
    }
    await this.save(estimate);
  }

  public async addNewItem(estimate: Estimate): Promise<void> {
    const newItem = new EstimateItem();
    newItem.id = uuid();
    estimate.items.push(newItem);
  }

  public async addNewItemAfter(
    estimate: Estimate,
    itemId: string,
    isActualResult: boolean = false,
    level: number = 0
  ): Promise<void> {
    const [items, i] = this.getItemListAndIndex(estimate, itemId);
    if (i < 0) {
      await this.addNewItem(estimate);
    } else {
      const newItem = new EstimateItem();
      const uuidValue = uuid();
      newItem.id = uuidValue;
      //BRV-397
      if (isActualResult) {
        newItem.isEditable = true;
        newItem.tempUuid = uuidValue;
        newItem.level = level;
        newItem.isFromActualResult = true;
      }
      items.splice(i + 1, 0, newItem);
    }
  }

  private getItemListAndIndex(
    estimate: Estimate,
    itemId: string
  ): [EstimateItem[], number] {
    let [itemList, index] = this.findItemListAndIndex(estimate, itemId);
    if (index >= 0) {
      return [itemList, index];
    }
    if (estimate.children.length > 0) {
      for (let i = 0; i < estimate.children.length; i++) {
        let estimateChild = estimate.children[i];
        [itemList, index] = this.findItemListAndIndex(estimateChild, itemId);
        if (index >= 0) {
          return [itemList, index];
        }
      }
    }
    return [itemList, index];
  }

  private findItemListAndIndex(
    estimate: Estimate,
    itemId: string
  ): [EstimateItem[], number] {
    if (estimate.items) {
      for (let i = 0; i < estimate.items.length; i++) {
        if (estimate.items[i].id === itemId) {
          return [estimate.items, i];
        }
        if (estimate.items[i] && estimate.items[i].items) {
          for (let j = 0; j < estimate.items[i].items.length; j++) {
            if (estimate.items[i].items[j].id === itemId) {
              return [estimate.items[i].items, j];
            }
            if (
              estimate.items[i].items[j] &&
              estimate.items[i].items[j].items
            ) {
              for (
                let k = 0;
                k < estimate.items[i].items[j].items.length;
                k++
              ) {
                if (estimate.items[i].items[j].items[k].id === itemId) {
                  return [estimate.items[i].items[j].items, k];
                }
              }
            }
          }
        }
      }
    }
    return [[] as EstimateItem[], -1];
  }

  public async removeItem(
    estimate: Estimate,
    itemId: string
  ): Promise<Estimate> {
    const [items, i] = this.getItemListAndIndex(estimate, itemId);
    if (i < 0) {
      return estimate;
    }
    items.splice(i, 1);
    return await this.calculateTotal(estimate);
  }

  public async removeAllItems(estimate: Estimate): Promise<Estimate> {
    estimate.items = [];
    return await this.calculateTotal(estimate);
  }

  public async destroy(id: number): Promise<void> {
    await this.estimates.destroy(id);
  }

  public async csvDownload(id: number): Promise<string> {
    return await this.estimates.csvDownload(id);
  }

  public async calculateTotal(params: Estimate): Promise<Estimate> {
    const paymentMethod = (params as any).paymentMethod;
    const estimatePaymentMethods = (params as any).estimatePaymentMethods;
    const estimate = toEstimate(params);
    const json: any = estimate.toJSON();
    json.paymentMethod = paymentMethod;
    json.estimatePaymentMethods = estimatePaymentMethods;
    return json;
  }

  /**
   * 発注額を自動計算する
   */
  public async calculateInvoice(
    estimate: Estimate,
    profitRate: number,
    idsSelected: Set<string>,
    numberRound: number,
    typeRound: string
  ): Promise<Estimate> {
    const lossRate = new Decimal(1)
      .minus(profitRate)
      .mul(100)
      .toNumber();

    const items = estimate.items.map(item =>
      this.roundUnitLossLowest(
        item,
        lossRate,
        idsSelected,
        numberRound,
        typeRound
      )
    );

    let children = await Promise.all(
      (estimate.children || []).map(async child =>
        this.calculateInvoice(
          child,
          profitRate,
          idsSelected,
          numberRound,
          typeRound
        )
      )
    );
    return await this.calculateTotal({ ...estimate, items, children });
  }

  private roundUnitLossLowest(
    estimateItem: EstimateItem,
    lossRate: number,
    idSelected: Set<string>,
    numberRound: number,
    typeRound: string
  ): EstimateItem {
    if (this.hasChildren(estimateItem)) {
      return this.roundForChildren(
        estimateItem,
        lossRate,
        idSelected,
        numberRound,
        typeRound
      );
    } else {
      return this.roundForSingleItem(
        estimateItem,
        lossRate,
        idSelected,
        numberRound,
        typeRound
      );
    }
  }

  private hasChildren(item: EstimateItem): boolean {
    return item.items && item.items.length > 0;
  }

  private roundForChildren(
    estimateItem: EstimateItem,
    lossRate: number,
    idSelected: Set<string>,
    numberRound: number,
    typeRound: string
  ): EstimateItem {
    const items = estimateItem.items.map(item =>
      this.roundUnitLossLowest(
        item,
        lossRate,
        idSelected,
        numberRound,
        typeRound
      )
    );
    return {
      ...estimateItem,
      items
    };
  }

  private roundForSingleItem(
    estimateItem: EstimateItem,
    lossRate: number,
    idSelected: Set<string>,
    numberRound: number,
    typeRound: string
  ): EstimateItem {
    if (idSelected.has(estimateItem.id.toString())) {
      const unitLoss = this.calculateUnitLoss(
        estimateItem.unitPrice,
        lossRate,
        numberRound,
        typeRound
      );
      return {
        ...estimateItem,
        unitLoss
      };
    } else {
      return { ...estimateItem };
    }
  }

  private calculateUnitLoss(
    unitPrice: number,
    lossRate: number,
    numberRound: number,
    typeRound: string
  ): number {
    return roundPrice(
      numberRound,
      typeRound,
      new UnitPrice(unitPrice).loss(lossRate)
    );
  }

  public async duplicate(id: number): Promise<number> {
    const origin = await this.estimates.find(id);
    if (!origin) {
      return await this.save(new Estimate());
    }
    return await this.copy(origin.id);
  }

  private changeItemIds(items: any[]) {
    if (items.length === 0) return;
    items.forEach(item => {
      item.id = uuid();
      this.changeItemIds(item.items);
    });
  }

  public getIdsSelected(
    estimate: Estimate,
    estimateRowSelected: Set<string>
  ): Set<string> {
    let result: Set<string> = new Set(estimateRowSelected);
    this.addIdsFromItems(estimate.items, result);
    estimate.children.forEach(child => {
      this.addIdsFromItems(child.items, result);
    });
    return result;
  }

  private addIdsFromItems(items: EstimateItem[], result: Set<string>) {
    items.forEach(item => {
      if (item.items.length > 0) {
        this.addIdsFromItems(item.items, result);
        if (item.items.some(itemChild => result.has(itemChild.id.toString()))) {
          result.add(item.id.toString());
        }
      } else {
        if (result.has(item.id.toString())) {
          result.add(item.id.toString());
        }
      }
    });
  }

  public addChild(parent: Estimate, child: Estimate) {
    parent.subtotal += child.subtotal;
    parent.loss += child.loss;
    parent.discount += child.discount;
    parent.tax = new TotalPrice(parent.subtotal).tax(parent.taxRate).toNumber();
    parent.total = new TotalPrice(parent.subtotal)
      .withTax(parent.taxRate)
      .toNumber();
  }

  public removeChild(parent: Estimate, child: Estimate) {
    parent.subtotal -= child.subtotal;
    parent.loss -= child.loss;
    parent.discount -= child.discount;
    parent.tax = new TotalPrice(parent.subtotal).tax(parent.taxRate).toNumber();
    parent.total = new TotalPrice(parent.subtotal)
      .withTax(parent.taxRate)
      .toNumber();
  }

  public saveEstimates(estimates: Estimate[]) {
    return this.estimates.updateEstimateList(estimates);
  }
}
